<template>
  <div class="text-left">
    <RejectCommentLayout :comment-text="reject_comment" />
    <v-card>
      <v-form class="pa-6" ref="form">
        <div class="required-label">
          <h5 class="mb-2">{{ $t("form.title") }}</h5>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.title"
              :label="''"
              :placeholder="$t('form.title')"
              required
              outlined
              :readonly="disabledForm"
              :rules="rules.char_required_max_250"
              :counter="250"
              @change="handleChange('title')"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4" class="py-0">
            <div class="required-label">
              <h5 class="mb-2">{{ $t('form.publication.language') }}</h5>
              <v-chip x-small outlined color="primary">
                {{ $t("form.mandatory") }}
              </v-chip>
            </div>
            <v-select
              v-model="currentPublication.language"
              :items="languageOptions"
              item-text="text"
              item-value="value"
              :label="''"
              :placeholder="$t('form.publication.language')"
              :rules="rules.fieldRequired"
              :readonly="disabledForm"
              outlined
              @change="handleChange('language')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.is_external"
              :label="$t('form.publication.external')"
              :readonly="disabledForm"
              @change="handleChange('is_external')"
            />
          </v-col>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.hidden"
              :label="$t('form.hidden')"
              :readonly="disabledForm"
              @change="handleChange('hidden')"
            />
          </v-col>
          <v-col cols="12" md="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.featured"
              :label="$t('form.featured')"
              :readonly="disabledForm"
              @change="handleChange('featured')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <div
              class="minerva-sync-panel"
              :style="{ borderLeftColor: minervaStatusBorderColor(currentPublication.minerva_sync_status) }"
            >
            <h5 class="py-0">
              {{ $t('form.publication.minervaSyncStatus') }}
            </h5>
            <p class="caption text--secondary mb-3">
              {{ $t('form.publication.minervaSyncHelp') }}
            </p>
            <v-select
              class="mb-4"
              v-model="currentPublication.minerva_sync_option"
              :items="minervaSyncOptions"
              item-text="text"
              item-value="value"
              :label="$t('form.publication.minervaVisibility')"
              :hint="$t('form.publication.minervaVisibilityHelp')"
              persistent-hint
              :readonly="disabledForm"
              outlined
              clearable
              @change="handleChange('minerva_sync_option')"
            />
            <div class="d-flex flex-column">
              <v-chip
                v-if="currentPublication.minerva_sync_status"
                small
                class="align-self-start mb-1"
                :color="minervaStatusColor(currentPublication.minerva_sync_status)"
                :text-color="minervaStatusTextColor(currentPublication.minerva_sync_status)"
              >
                {{ minervaStatusLabel(currentPublication.minerva_sync_status) }}
              </v-chip>
              <span
                class="caption"
                v-if="minervaStatusDescription(currentPublication.minerva_sync_status, currentPublication)"
              >
                {{ minervaStatusDescription(currentPublication.minerva_sync_status, currentPublication) }}
              </span>
              <v-tooltip
                top
                v-if="availableMinervaUrl(currentPublication)"
                class="mt-2 align-self-start"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :href="availableMinervaUrl(currentPublication)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                {{ $t('form.publication.minerva.summary.available') }}
              </v-tooltip>
              <span
                class="caption mt-1"
                v-if="currentPublication.minerva_sync_status === 'missing-data' && minervaMetadataSummary(currentPublication)"
              >
                {{ minervaMetadataSummary(currentPublication) }}
              </span>
            </div>
            </div>
          </v-col>
        </v-row>
        <h5 class="form-section-title">
          {{ $t("form.abstract") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="currentPublication.abstract"
              :label="$t('form.abstract')"
              outlined
              :readonly="disabledForm"
              :rules="rules.char_max_6500"
              :counter="6500"
              @change="handleChange('abstract')"
            ></v-textarea>
          </v-col>
        </v-row>
        <h5 class="form-section-title">{{ $t("form.keywords") }}</h5>

        <ArrayFieldForm
          v-model="currentPublication.keywords"
          :read-only="disabledForm"
          name-field="keywords"
          :label="$t('form.keywords')"
          @change="handleChange('keywords')"
        />
        <v-row>
          <v-col cols="12" sm="8" class="py-0">
            <div class="required-label">
              <h5 class="mb-2">{{ $t("form.authors_text") }}</h5>
              <v-chip x-small outlined color="primary">
                {{ $t("form.mandatory") }}
              </v-chip>
            </div>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="currentPublication.authors_text"
                  :label="$t('form.authors_text')"
                  required
                  outlined
                  :readonly="disabledForm"
                  :rules="[...rules.fieldRequired, ...rules.char_max_400]"
                  :counter="400"
                  @change="handleChange('authors_text')"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="4" class="py-0">
            <div class="required-label">
              <h5 class="mb-2">{{ $t("form.num_authors") }}</h5>
              <v-chip x-small outlined color="primary">
                {{ $t("form.mandatory") }}
              </v-chip>
            </div>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="currentPublication.num_authors"
                  :label="''"
                  :placeholder="$t('form.num_authors')"
                  required
                  :readonly="disabledForm"
                  outlined
                  :rules="rules.numAuthorsRules"
                  type="number"
                  @change="handleChange('num_authors')"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <div class="required-label">
          <h5 class="mb-2">{{ $t("form.authorsrel") }}</h5>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <SelectPeopleComboBox
          :disabled-form="disabledForm"
          :items-selected="currentPublication.authorsrel"
          :text-authors="currentPublication.authors_text"
          @change="handleChange('authorsrel')"
        />
        <h5 class="form-section-title">
          {{ $t("form.acknowledgements") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-textarea
              v-model="currentPublication.acknowledgements"
              :readonly="disabledForm"
              :label="$t('form.acknowledgements')"
              outlined
              :rules="rules.char_max_3500"
              :counter="3500"
              @change="handleChange('acknowledgements')"
            ></v-textarea>
          </v-col>
        </v-row>
        <h5 class="form-section-title">
          {{ $t('form.doi') }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.doi"
              :label="$t('form.doi')"
              required
              :readonly="disabledForm"
              outlined
              :rules="rules.doiRules"
              @change="handleChange('doi')"
            ></v-text-field>
          </v-col>
        </v-row>
        <h5 class="form-section-title">
          {{ $t('form.isbn') }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.isbn"
              :label="$t('form.isbn')"
              required
              outlined
              :readonly="disabledForm"
              :rules="rules.isbnRules"
              @change="handleChange('isbn')"
            ></v-text-field>
          </v-col>
        </v-row>
        <h5 class="form-section-title">
          {{ $t("form.publication.research_areas") }}
        </h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-select
              :items="researchAreas"
              v-model="currentPublication.research_areas"
              :label="$t('form.publication.research_areas')"
              required
              :item-text="'name.' + $i18n.locale"
              item-value="id"
              outlined
              :readonly="disabledForm"
              multiple
              :rules="rules.fieldRequired"
              @change="handleChange('research_areas')"
            ></v-select>
          </v-col>
        </v-row>
        <div class="required-label">
          <h5 class="mb-2">{{ $t("collaborations") }}</h5>
          <v-chip x-small outlined color="primary">
            {{ $t("form.mandatory") }}
          </v-chip>
        </div>
        <v-row>
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.regional"
              :label="$t('form.publication.regional')"
              :readonly="disabledForm"
              :true-value="true"
              :false-value="false"
              :indeterminate="currentPublication.regional === ''"
              :rules="rules.checkboxRequired"
              @change="handleChange('regional')"
            />
          </v-col>
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.national"
              :label="$t('form.publication.national')"
              :readonly="disabledForm"
              :true-value="true"
              :false-value="false"
              :indeterminate="currentPublication.national === ''"
              :rules="rules.checkboxRequired"
              @change="handleChange('national')"
            />
          </v-col>
          <v-col cols="12" lg="4" class="py-0">
            <v-checkbox
              class="big"
              v-model="currentPublication.international"
              :label="$t('form.publication.international')"
              :readonly="disabledForm"
              :true-value="true"
              :false-value="false"
              :indeterminate="currentPublication.international === ''"
              :rules="rules.checkboxRequired"
              @change="handleChange('international')"
            />
          </v-col>
        </v-row>
        <h5 class="form-section-title">Links</h5>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.links.publisher"
              :label="$t('label.links.publisher')"
              outlined
              :readonly="disabledForm"
              @change="handleChange('links')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.links.institutional_repository"
              :label="$t('label.links.institutional_repository')"
              outlined
              :readonly="disabledForm"
              @change="handleChange('links')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.links.personal_repository"
              :label="$t('label.links.personal_repository')"
              outlined
              :readonly="disabledForm"
              @change="handleChange('links')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="currentPublication.links.teseo"
              :label="$t('label.links.teseo')"
              outlined
              :readonly="disabledForm"
              @change="handleChange('links')"
            />
          </v-col>
        </v-row>

        <h5>{{ $t("form.files") }}</h5>
        <v-alert
          v-if="modOp_id === null"
          dense
          text
          type="info"
          class="without-margin-bottom"
        >
          {{ $t("form.publication.files.modify_info") }}
        </v-alert>
        <v-card min-height="200px" elevation="0">
          <UploadFiles
            :read-only="disabledForm"
            object_type="publication"
            :id="this.modOp_id"
            :final="false"
            @change-files="handleChangeFile()"
          />
          <v-row>
            <v-overlay
              :opacity="0.85"
              :absolute="true"
              :value="modOp_id === null"
              color="#ffffff"
            >
              <v-btn color="secondary" @click="saveform_trigger = !saveform_trigger">
                {{ $t("button.saveForm") }}
              </v-btn>
            </v-overlay>
          </v-row>
        </v-card>
        <div v-if="currentPublication.article !== null">
          <ModifyArticle
            :disabled-form="disabledForm"
            :publication="currentPublication"
            @handle-change="handleChangeFieldComponent"
          />
        </div>
        <div v-if="currentPublication.communication !== null">
          <ModifyCommunication
            :disabled-form="disabledForm"
            :publication="currentPublication"
            @handle-change="handleChangeFieldComponent"
          />
        </div>
        <div v-if="currentPublication.thesis !== null">
          <ModifyThesis
            :disabled-form="disabledForm"
            :publication="currentPublication"
            @handle-change="handleChangeFieldComponent"
          />
        </div>
        <div v-if="currentPublication.bookandchapter !== null">
          <ModifyBookAndChapter
            :book_editors="book_editors"
            :disabled-form="disabledForm"
            :publication="currentPublication"
            @handle-change="handleChangeFieldComponent"
          />
        </div>
        <v-divider />

        <UserFormActions
          form_name_text="Modify basic data"
          :form_name="form_name"
          :refForm="$refs.form"
          :object_type="object_type"
          :data="processData"
          :state="state"
          :saveform_trigger="saveform_trigger"
          @change-state="changeState"
          :paginated-form="true"
          :updated-form="true"
          :changed-fields="changedFields"
          :type-change-fields="typechangeFields"
          class="text-center mt-6"
        />
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import RejectCommentLayout from "../../layouts/RejectCommentLayout";
import UserFormActions from "../../action_buttons/UserFormActions";
import changeState from "../../../mixins/changeState";
import validationRules from "../../../mixins/validationRules";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import SelectPeopleComboBox from "../../reusables/SelectPeopleComboBox";
import ModifyArticle from "./ModifyArticle";
import ModifyCommunication from "./ModifyCommunication";
import ModifyThesis from "./ModifyThesis";
import ModifyBookAndChapter from "./ModifyBookAndChapter";
import UploadFiles from "../../reusables/UploadFiles";
import publication_model from "../../../mixins/model_utils/publication_model";
import minervaDisplay from "../../../mixins/minervaDisplay";

const READ_ONLY_MINERVA_FIELDS = [
  "minerva_sync_status",
  "minerva_sync_metadata",
  "minerva_last_checked_at"
];
export default {
  mixins: [changeState, validationRules, publication_model, minervaDisplay],
  components: {
    ModifyCommunication,
    ModifyArticle,
    ArrayFieldForm,
    SelectPeopleComboBox,
    RejectCommentLayout,
    UserFormActions,
    ModifyThesis,
    ModifyBookAndChapter,
    UploadFiles
  },

  data() {
    return {
      saveform_trigger: false,
      originalPublication: "",
      currentPublication: {
        title: "",
        abstract: "",
        keywords: "",
        acknowledgements: "",
        doi: "",
        isbn: "",
        authors_text: "",
        authorsrel: [],
        research_areas: [],
        regional: false,
        national: false,
        international: false,
        is_external: false,
        hidden: false,
        featured: false,
        language: "en",
        minerva_sync_option: "restricted",
        minerva_sync_status: "pending-check",
        minerva_sync_metadata: {},
        minerva_last_checked_at: null,
        links: {
          publisher: "",
          institutional_repository: "",
          personal_repository: "",
          teseo: ""
        },
        article: null,
        bookandchapter: null,
        thesis: null,
        communication: null
      },
      changedFields: [],
      book_editors: [],
      form_name: "modifyBasicDataForm",
      menu: false,
      state: "",
      reject_comment: "",
      modOp_id: null,
      object_id: null,
      object_type: "publication",
      publication: {
        title: "",
        abstract: "",
        keywords: "",
        acknowledgements: "",
        doi: "",
        isbn: "",
        authors_text: "",
        authorsrel: [],
        research_areas: [],
        regional: false,
        national: false,
        international: false,
        is_external: false,
        hidden: false,
        featured: false,
        language: "en",
        minerva_sync_option: "restricted",
        minerva_sync_status: "pending-check",
        minerva_sync_metadata: {},
        minerva_last_checked_at: null,
        links: {
          publisher: "",
          institutional_repository: "",
          personal_repository: "",
          teseo: ""
        }
      }
    };
  },

  created() {
    this.getIdPublication();
    this.getResearchAreas();
    this.getLanguageOptions();
    this.getMinervaSyncOptions();
  },

  watch : {'$route' () {this.modOp_id = Number(this.$route.params.id)} },

  computed: {
    typechangeFields() {
      if (this.currentPublication.article !== null) {
        return "article";
      } else if (this.currentPublication.bookandchapter !== null) {
        return "bookandchapter";
      } else if (this.currentPublication.thesis !== null) {
        return "thesis";
      } else if (this.currentPublication.communication !== null) {
        return "communication";
      } else {
        return "";
      }
    },
    disabledForm() {
      let states = ["VD", "PA", "PV", "PC"];
      return states.includes(this.state);
    },
    processData() {
      let changeData = {};
      this.changedFields.forEach(field => {
        if (typeof field === "object") {
          for (const subField in field) {
            if (READ_ONLY_MINERVA_FIELDS.includes(subField)) {
              continue;
            }
            for (const value in field[subField]) {
              if (changeData[subField] === undefined) {
                changeData[subField] = {};
              }
              changeData[subField][value] = this.currentPublication[subField][
                value
              ];
            }
          }
        } else {
          if (READ_ONLY_MINERVA_FIELDS.includes(field)) {
            return;
          }
          if (field === 'files'){
            changeData[field] = 'files_modified'
          }else{
          changeData[field] = this.currentPublication[field];
          }
        }
      });
      if (!this.disabledForm && changeData["research_areas"] !== undefined) {
        changeData["research_areas"] = changeData[
          "research_areas"
        ].filter(area => Number.isInteger(area));
      }
      let data = {
        title: this.currentPublication.title,
        object_type: this.object_type,
        form_name: this.form_name,
        object_id: this.object_id,
        data: {
          user: {
            publication: changeData
          }
        }
      };
      return data;
    }
  },

  methods: {
    handleChange(changedField) {
      if (READ_ONLY_MINERVA_FIELDS.includes(changedField)) {
        return;
      }
      const results = this.changedFields.filter(
        field => field !== changedField
      );
      if(changedField === 'files'){
        results.push(changedField)
      }
      else if (
        this.originalPublication[changedField] !==
        this.currentPublication[changedField]
      ) {
        results.push(changedField);
      }
      this.changedFields = results;
    },
    handleChangeFieldComponent(component, value) {
      if (value === "editors") {
        this.currentPublication[component][value] = this.book_editors;
      }
      if (
        this.originalPublication[component][value] !==
        this.currentPublication[component][value]
      ) {
        let data = {
          [component]: {
            [value]: this.currentPublication[component][value]
          }
        };

        if (
          this.changedFields.find(item => item[component] != null) !== undefined
        ) {
          this.changedFields.find(item => item[component] != null)[component][
            value
          ] = data[component][value];
          this.changedFields.push();
        } else {
          this.changedFields.push(data);
        }
      }
    },
    handleChangeFile(){
      this.handleChange('files')
      this.saveform_trigger = !this.saveform_trigger
    },
    normalizePublicationState(target) {
      if (!target) {
        return;
      }
      if (target.hidden === undefined) {
        this.$set(target, "hidden", false);
      }
      if (target.featured === undefined) {
        this.$set(target, "featured", false);
      }
      if (!target.language) {
        this.$set(target, "language", "en");
      }
      if (target.minerva_sync_option === undefined) {
        this.$set(target, "minerva_sync_option", "restricted");
      }
      if (!target.minerva_sync_status) {
        this.$set(target, "minerva_sync_status", "pending-check");
      }
      if (!target.minerva_sync_metadata || typeof target.minerva_sync_metadata !== "object") {
        this.$set(target, "minerva_sync_metadata", {});
      }
      if (target.minerva_last_checked_at === undefined) {
        this.$set(target, "minerva_last_checked_at", null);
      }
    },
    getIdPublication() {
      if (this.$route.meta.update) {
        this.object_id = Number(this.$route.params.id);
        this.getPublication();
      } else {
        let path =
          this.$store.state.endpoint +
          "/user/moderatedOperations/publication/" +
          this.$route.params.id;
        axios
          .get(path)
          .then(response => {
            this.object_id = response.data.object_id;
          })
          .finally(() => this.getPublication());
      }
    },
    getPublication() {
      let path = this.$store.state.endpoint + "/publication/" + this.object_id;
      axios
        .get(path)
        .then(response => {
          this.originalPublication = response.data;
          this.currentPublication = this.$R.clone(response.data);
          this.normalizePublicationState(this.originalPublication);
          this.normalizePublicationState(this.currentPublication);
        })
        .finally(() => this.getModOperation())
        .catch(error => {
          console.log(error);
        });
    },
    getModOperation() {
      if (!this.$route.meta.update) {
        let path =
          this.$store.state.endpoint +
          "/user/moderatedOperations/publication/" +
          this.$route.params.id;
        axios
          .get(path)
          .then(response => {
            this.modOp_id = response.data.id;
            this.title = response.data.title;
            this.state = response.data.state;
            this.reject_comment = response.data.reject_comment;
            for (const field in response.data.data.user.publication) {
              if (READ_ONLY_MINERVA_FIELDS.includes(field)) {
                continue;
              }
              if (
                typeof response.data.data.user.publication[field] ===
                  "object" &&
                !Array.isArray(response.data.data.user.publication[field])
              ) {
                for (const value in response.data.data.user.publication[
                  field
                ]) {
                  if (this.currentPublication[field] === undefined) {
                    this.currentPublication[field] = {};
                  }
                  this.currentPublication[field][value] =
                    response.data.data.user.publication[field][value];
                  let data = {
                    [field]: {
                      [value]: this.currentPublication[field][value]
                    }
                  };
                  this.changedFields.push(data);
                }
              } else {
                this.changedFields.push(field);
                this.currentPublication[field] =
                  response.data.data.user.publication[field];
              }
            }
            this.normalizePublicationState(this.currentPublication);
          })
          .finally(() => {
            this.getEditors();
          })
          .catch(error => {
            console.log(error);
          });
      } else {
        this.getEditors();
      }
    },

    getEditors() {
      if (
        this.currentPublication.bookandchapter != null &&
        this.currentPublication.bookandchapter.editors.length
      ) {
        const path = this.$store.state.endpoint + "/person";
        axios
          .get(path)
          .then(response => {
            this.currentPublication.bookandchapter.editors.forEach(id => {
              const item = response.data.find(
                itemResponse => itemResponse.id === id
              );
              if (item !== undefined) {
                this.book_editors.push({
                  id: item.id,
                  name: item.preferred_name.given_name
                });
              }
            });
          })
          .catch(error => {
            console.log(error);
          });
      }
    },
  }
};
</script>

<style scoped>
.required-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.required-label .v-chip {
  margin-left: 8px;
}

.form-section-title {
  margin-bottom: 0.85rem;
}

.minerva-sync-panel {
  border-left: 4px solid var(--v-primary-base);
  padding: 0.25rem 0 0.35rem 1rem;
  margin-bottom: 0.5rem;
}
</style>
